import React from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    number: 1,
    title: 'Court Bookings',
    body: 'All bookings are subject to court availability. A booking is only confirmed with successful payment and issuance of a booking confirmation. Customers are responsible for ensuring that booking details, including date, time, and court selection, are accurate before payment.',
  },
  {
    number: 2,
    title: 'Payment',
    body: 'Full payment is required at the time of booking unless otherwise stated. Prices displayed on the website are in the applicable local currency. SmashZone reserves the right to revise pricing and promotional offers at any time without prior notice.',
  },
  {
    number: 3,
    title: 'Cancellation & Refund Policy',
    body: 'All sales are final. We do not offer refunds or cancellations once payment has been processed. No-show booked session may be eligible for rescheduling subject to management approval and availability of court.',
  },
  {
    number: 4,
    title: 'Late Arrival',
    body: 'Booked court time cannot be extended due to late arrival. The booking will end at the scheduled time regardless of actual arrival time.',
  },
  {
    number: 5,
    title: 'Court Usage & Conduct',
    body: 'Players must wear appropriate sports attire and non-marking court shoes. Users must use all facilities responsibly and follow instructions provided by SmashZone staff. Any abusive, disruptive, unsafe, unlawful, or inappropriate conduct may result in removal from the premises without refund. Smoking, illegal substances, weapons, or dangerous activities are strictly prohibited.',
  },
  {
    number: 6,
    title: 'Safety & Personal Responsibility',
    body: 'Participation in badminton, pickleball, and related activities carries inherent risks of injury. All users participate at their own risk and are responsible for ensuring they are medically fit to engage in sporting activities. Parents or legal guardians are responsible for minors using the facilities.',
  },
  {
    number: 7,
    title: 'Damage to Property',
    body: 'Customers are responsible for any loss or damage caused to SmashZone property, facilities, equipment, or fixtures due to misuse, negligence, or intentional acts. SmashZone reserves the right to recover repair or replacement costs from the party responsible.',
  },
  {
    number: 8,
    title: 'Limitation of Liability',
    body: 'To the fullest extent permitted by law, SmashZone shall not be liable for any injury, loss, damage, theft, delay, interruption, or consequential loss arising from the use of our facilities, website, or services. Users are responsible for their personal belongings at all times.',
  },
  {
    number: 9,
    title: 'Website & Account Usage',
    body: 'Users must provide accurate information when creating an account or making a booking. SmashZone reserves the right to suspend or terminate any account involved in fraudulent, abusive, or unlawful activity. Unauthorized use of the website or attempts to interfere with its operation are prohibited.',
  },
  {
    number: 10,
    title: 'Privacy and Data Protection',
    body: 'Personal information collected through our website will be used for booking management, customer support, marketing communications (where permitted), and operational purposes in accordance with our Privacy Policy. We only collect necessary information for booking management: Name, email and phone number. SmashZone does not store your card information. Card & Pocket App payments are accepted securely via the 3rd-party payment gateway service provider.',
  },
  {
    number: 11,
    title: 'Force Majeure',
    body: 'SmashZone shall not be liable for any failure or delay in providing services caused by events beyond our reasonable control, including power failures, natural disasters, government actions, public health emergencies, or technical disruptions.',
  },
  {
    number: 12,
    title: 'Amendments',
    body: 'SmashZone may update these Terms & Conditions from time to time. Updated terms will be posted on our website and take effect immediately upon publication.',
  },
  {
    number: 13,
    title: 'Governing Law',
    body: 'These Terms & Conditions shall be governed by and construed in accordance with the laws of Brunei Darussalam and any disputes shall be subject to the exclusive jurisdiction of the courts of that jurisdiction.',
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-600 via-green-700 to-teal-800 py-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-black font-heading uppercase text-white mb-3">
            Terms & Conditions
          </h1>
          <p className="text-emerald-100 text-sm">Last Updated: 22nd August 2026</p>
          <p className="text-emerald-100 text-sm mt-1">SmashZone Enterprise</p>
        </div>
      </div>

      {/* Intro */}
      <div className="max-w-3xl mx-auto w-full px-6 pt-10 pb-4">
        <p className="text-gray-600 leading-relaxed">
          Welcome to SmashZone Enterprise ("SmashZone", "we", "our", or "us"). By accessing our
          website, creating an account, or making a court booking through{' '}
          <a
            href="https://www.smashzone.asia"
            className="text-emerald-700 underline"
            target="_blank"
            rel="noreferrer"
          >
            www.smashzone.asia
          </a>
          , you agree to be bound by these Terms & Conditions.
        </p>
      </div>

      {/* Sections */}
      <div className="max-w-3xl mx-auto w-full px-6 pb-10 flex-1">
        <div className="space-y-8">
          {sections.map((s) => (
            <div key={s.number}>
              <h2 className="text-base font-bold text-gray-900 mb-2">
                {s.number}. {s.title}
              </h2>
              <p className="text-gray-600 leading-relaxed text-sm">{s.body}</p>
            </div>
          ))}

          {/* Contact Us */}
          <div className="border-t border-gray-200 pt-8">
            <h2 className="text-base font-bold text-gray-900 mb-3">Contact Us</h2>
            <div className="text-sm text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">SmashZone Enterprise</p>
              <p>
                Website:{' '}
                <a
                  href="https://smashzone.asia"
                  className="text-emerald-700 underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  https://smashzone.asia
                </a>
              </p>
              <p>
                Email:{' '}
                <a href="mailto:smashzonebn@gmail.com" className="text-emerald-700 underline">
                  smashzonebn@gmail.com
                </a>
              </p>
              <p>Phone: +673 7438775</p>
              <p>Address: 1st floor, Aman Hills Shopping Centre, Spg 30, Jln Aman, BSB, Brunei BC3615</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-primary text-primary-foreground py-8 mt-12">
        <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-sm opacity-75">
            © 2026 SmashZone. Book your badminton and pickleball courts with ease.
          </p>
          <Link to="/" className="text-sm opacity-75 hover:opacity-100 transition-opacity">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
